import React, { useState } from 'react';

import { Button } from '@lightspeed/flame/Button';
import axios from 'axios';

import Modules from 'root/apps/shared_modules/Modules';

import { B2B_GROUP_SEACH, findMSRP, flashError } from '../utils';
import { B2BDuplicateDialog } from './B2BDuplicateDialog';
import { B2BMatrixDialog } from './B2BMatrixDialog';
import { useB2BInsert, useMatrixImport } from './ImportHooks';
import { B2BMatrixContextItem, ButtonProps } from './types';

export function B2BImportButton({
  b2bItem,
  purchaseID,
  vendorID,
  importInProgressRef,
}: ButtonProps) {
  const b2bMsrp = findMSRP(b2bItem);
  const [thisIsLoading, setThisIsLoading] = useState(false);

  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [matrixDialogOpen, setMatrixDialogOpen] = useState(false);
  const [matrixItems, setMatrixItems] = useState<B2BMatrixContextItem[]>([]);
  const [matrixID, setMatrixID] = useState<string>('');

  const importMatrix = useMatrixImport(purchaseID, vendorID);
  const insertItem = useB2BInsert(b2bItem, purchaseID, vendorID);

  const closeDuplicateDialog = () => setDuplicateDialogOpen(false);
  const closeMatrixDialog = () => setMatrixDialogOpen(false);

  const startWorking = () => {
    importInProgressRef.current = true;
    setThisIsLoading(true);
  };

  const clearWorking = () => {
    window.hideNotifier('working');
    importInProgressRef.current = false;
    setThisIsLoading(false);
  };

  const handleImportClick = async () => {
    if (importInProgressRef.current) return;
    importInProgressRef.current = true;

    window.hideAllNotifiers();

    if (Modules.hasModule('catalog.b2b.matrix_integration')) {
      setThisIsLoading(true);

      try {
        // We check if the item is part of a matrix and show the matrix dialog.
        const groupResult = await axios.post(B2B_GROUP_SEACH, { groupID: b2bItem.group_id });
        const items = groupResult.data?.data;

        if (Array.isArray(items) && items.length > 1) {
          // Add the selected item first, then the rest without selection
          const wrapped = [
            {
              b2bItem,
              selected: true,
              cost: 0,
              price: 0,
              index: 0,
              previouslyImported: b2bItem.is_inventory_item,
            },
            ...items
              .filter((item) => item.id !== b2bItem.id)
              .map((item, index) => ({
                b2bItem: item,
                selected: item.is_inventory_item,
                cost: 0,
                price: 0,
                index: index + 1,
                previouslyImported: item.is_inventory_item,
              })),
          ];

          setMatrixItems(wrapped);
          setMatrixID(groupResult.data.matrixID === 'null' ? '' : groupResult.data.matrixID);
          setMatrixDialogOpen(true);

          importInProgressRef.current = false;
          setThisIsLoading(false);
          return;
        }
      } catch (error) {
        flashError();
        importInProgressRef.current = false;
        setThisIsLoading(false);
        return;
      }
    }

    // From here, we're dealing with non-matrix items

    // Previously imported items allow for merging
    if (b2bItem.is_inventory_item) {
      const itemID = b2bItem.inventory_item_details?.item_id;

      // If we're coming from a purchase order
      // We'll navigate to the item already imported
      if (purchaseID && itemID) {
        window.navigateToContent('view', 'item.views.item', itemID);
        return;
      }

      // Otherwise, we show the merge duplicate dialog
      importInProgressRef.current = false;
      setDuplicateDialogOpen(true);
      setThisIsLoading(false);
      return;
    }

    // Otherwise insert new
    doInsert();
  };

  const doInsert = async () => {
    startWorking();

    await insertItem();

    clearWorking();
  };

  const handleMatrixDialogConfirm = async (
    items: B2BMatrixContextItem[],
    matrixID: string,
    useDefaultCost: boolean
  ) => {
    closeMatrixDialog();

    startWorking();

    await importMatrix(items, matrixID, useDefaultCost);

    clearWorking();
  };

  return (
    <>
      <Button
        type="button"
        onClick={handleImportClick}
        style={{ padding: '6px 12px' }}
        loading={thisIsLoading}
      >
        Import
      </Button>
      {duplicateDialogOpen && (
        <B2BDuplicateDialog
          b2bItem={b2bItem}
          b2bMsrp={b2bMsrp}
          onCancel={closeDuplicateDialog}
          onConfirm={closeDuplicateDialog}
        />
      )}
      {matrixDialogOpen && (
        <B2BMatrixDialog
          purchaseID={purchaseID}
          matrixID={matrixID}
          matrixItems={matrixItems}
          onCancel={closeMatrixDialog}
          onConfirm={handleMatrixDialogConfirm}
        />
      )}
    </>
  );
}
